/**
 * AdjudicatarioHistorialModal v4.4.10 — Histórico de un adjudicatario.
 *
 * Muestra todos los contratos que una empresa ha ganado en PLACSP detectados
 * por SpotterSRS, con métricas agregadas (valor total, regiones, sectores,
 * contratos UTE, fondos UE…) y lista navegable.
 *
 * Usado desde OportunidadModal al pulsar "Ver histórico" junto al adjudicatario.
 */
import { useEffect, useState } from "react";
import api from "@/services/api";
import {
  Building2,
  TrendingUp,
  MapPin,
  Euro,
  Calendar,
  Award,
  Users,
  ExternalLink,
  Loader2,
  FileText,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";

function formatEUR(v) {
  const n = Number(v || 0);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} M€`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)} K€`;
  return `${n.toFixed(0)} €`;
}

function formatDate(s) {
  if (!s) return "-";
  try {
    return new Date(s).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return s;
  }
}

export default function AdjudicatarioHistorialModal({ open, onClose, adjudicatario, nif }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open) return;
    const key = nif || adjudicatario;
    if (!key) return;

    setLoading(true);
    api
      .get(`/adjudicatarios/${encodeURIComponent(key)}/historico`, {
        params: { limit_contratos: 50 },
      })
      .then((r) => setData(r.data))
      .catch((e) => {
        console.error("Error cargando histórico:", e);
        toast.error("No se pudo cargar el histórico");
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [open, adjudicatario, nif]);

  const m = data?.metricas || {};
  const contratos = data?.contratos || [];
  const regiones = data?.regiones_top || [];
  const sectores = data?.sectores_top || [];
  const timeline = data?.timeline_mensual || [];

  const total = m.total_contratos || 0;
  const esPartner = data?.identidad?.es_partner_srs;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-700 w-[95vw] max-w-5xl max-h-[92vh] overflow-hidden flex flex-col">
        <DialogHeader className="pb-3 border-b border-slate-800">
          <DialogTitle className="flex items-center gap-2 text-white">
            <Building2 className="w-5 h-5 text-teal-400" />
            <span className="truncate">{adjudicatario}</span>
            {esPartner && (
              <Badge className="bg-teal-500/20 text-teal-400 border-teal-400/40 text-[10px]">
                PARTNER
              </Badge>
            )}
          </DialogTitle>
          {nif && (
            <p className="font-mono text-xs text-slate-500 pl-7">{nif}</p>
          )}
        </DialogHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-teal-400" />
          </div>
        ) : !data || total === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center py-20 text-slate-500">
            <FileText className="w-12 h-12 mb-3" />
            <p>Sin contratos previos detectados</p>
            <p className="text-xs mt-1">
              Este adjudicatario aún no aparece en otras oportunidades PLACSP del CRM.
            </p>
          </div>
        ) : (
          <ScrollArea className="flex-1 pr-2">
            <div className="space-y-4 py-3">
              {/* KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 bg-slate-800/50 rounded-lg">
                  <p className="text-[10px] text-slate-500 uppercase">Contratos</p>
                  <p className="text-2xl font-bold font-mono text-white">{total}</p>
                </div>
                <div className="p-3 bg-slate-800/50 rounded-lg">
                  <p className="text-[10px] text-slate-500 uppercase">Valor total</p>
                  <p className="text-2xl font-bold font-mono text-green-400">
                    {formatEUR(m.valor_total)}
                  </p>
                </div>
                <div className="p-3 bg-slate-800/50 rounded-lg">
                  <p className="text-[10px] text-slate-500 uppercase">Importe medio</p>
                  <p className="text-2xl font-bold font-mono text-white">
                    {formatEUR(m.importe_medio)}
                  </p>
                </div>
                <div className="p-3 bg-slate-800/50 rounded-lg">
                  <p className="text-[10px] text-slate-500 uppercase">Máx. contrato</p>
                  <p className="text-2xl font-bold font-mono text-amber-400">
                    {formatEUR(m.importe_max)}
                  </p>
                </div>
              </div>

              {/* Flags */}
              <div className="flex flex-wrap gap-2">
                {m.con_fondos_ue > 0 && (
                  <Badge className="bg-blue-500/20 text-blue-400 border-blue-400/40">
                    🇪🇺 {m.con_fondos_ue} con fondos UE
                  </Badge>
                )}
                {m.es_ute_veces > 0 && (
                  <Badge className="bg-purple-500/20 text-purple-400 border-purple-400/40">
                    <Users className="w-3 h-3 mr-1" />
                    UTE en {m.es_ute_veces} contratos
                  </Badge>
                )}
                {m.pyme_veces > 0 && (
                  <Badge variant="outline" className="border-slate-600 text-slate-300">
                    PYME en {m.pyme_veces}
                  </Badge>
                )}
              </div>

              {/* Top regiones + sectores */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-3 bg-slate-800/30 rounded-lg">
                  <h4 className="text-sm font-semibold text-teal-400 mb-2 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Regiones top
                  </h4>
                  {regiones.length === 0 ? (
                    <p className="text-xs text-slate-500">Sin datos de región</p>
                  ) : (
                    <ul className="space-y-1">
                      {regiones.slice(0, 5).map((r) => (
                        <li key={r._id} className="flex items-center justify-between text-sm">
                          <span className="text-slate-300">{r._id}</span>
                          <span className="text-slate-500 text-xs">
                            {r.count} · {formatEUR(r.valor)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="p-3 bg-slate-800/30 rounded-lg">
                  <h4 className="text-sm font-semibold text-teal-400 mb-2 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Sectores SRS top
                  </h4>
                  {sectores.length === 0 ? (
                    <p className="text-xs text-slate-500">Sin clasificación</p>
                  ) : (
                    <ul className="space-y-1">
                      {sectores.slice(0, 5).map((s) => (
                        <li key={s._id} className="flex items-center justify-between text-sm">
                          <span className="text-slate-300">{s._id}</span>
                          <span className="text-slate-500 text-xs">
                            {s.count} · {formatEUR(s.valor)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* Timeline */}
              {timeline.length > 0 && (
                <div className="p-3 bg-slate-800/30 rounded-lg">
                  <h4 className="text-sm font-semibold text-teal-400 mb-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Actividad últimos 24 meses
                  </h4>
                  <div className="flex items-end gap-1 h-16 overflow-x-auto">
                    {timeline.map((t) => {
                      const max = Math.max(...timeline.map((x) => x.count));
                      const h = Math.max(4, (t.count / max) * 60);
                      return (
                        <div
                          key={t._id}
                          className="flex flex-col items-center justify-end min-w-[24px]"
                          title={`${t._id}: ${t.count} contratos · ${formatEUR(t.valor)}`}
                        >
                          <div
                            className="bg-teal-400/60 hover:bg-teal-400 w-5 rounded-t transition-colors"
                            style={{ height: `${h}px` }}
                          />
                          <span className="text-[8px] text-slate-500 mt-1 rotate-45 origin-left">
                            {t._id.slice(5)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Tabla de contratos */}
              <div className="p-3 bg-slate-800/30 rounded-lg">
                <h4 className="text-sm font-semibold text-teal-400 mb-2 flex items-center gap-2">
                  <Award className="w-4 h-4" />
                  Últimos contratos ganados ({contratos.length} de {total})
                </h4>
                <div className="space-y-1 max-h-[400px] overflow-y-auto">
                  {contratos.map((c) => (
                    <a
                      key={c.oportunidad_id || c.ref_placsp}
                      href={`/oportunidades?open=${c.oportunidad_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block p-2 rounded hover:bg-slate-700/40 transition-colors border border-transparent hover:border-teal-400/30"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-200 truncate">
                            {c.titulo || "(sin título)"}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500">
                            <span>{formatDate(c.fecha_adjudicacion)}</span>
                            {c.lugar_ejecucion && <span>· {c.lugar_ejecucion}</span>}
                            {c.tipo_srs && <span>· {c.tipo_srs.split(" / ")[0]}</span>}
                            {c.tiene_fondos_ue && (
                              <span className="text-blue-400">· 🇪🇺 {c.fondos_ue || "UE"}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-0.5 shrink-0">
                          <span className="font-mono text-green-400 text-sm font-semibold">
                            {formatEUR(c.importe)}
                          </span>
                          <ExternalLink className="w-3 h-3 text-slate-500" />
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}
